import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Button, Card, CardBody, CardHeader, Badge } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faPlus,
    faChevronDown,
    faChevronRight,
    faCog
} from "@fortawesome/free-solid-svg-icons";
import { Col, Row } from "reactstrap";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import BreadCrumbs from "../../../../components/BreadCrumbs";
import {
    useGetDimensionsQuery,
    useLazyGetMenuTypesQuery,
    useDeleteDimensionMutation,
    useDeleteDimensionValueMutation
} from "../../../../redux/services/CustomTypeService";
import { useSelector } from "react-redux";
import ButtonComponent from "../../../../components/ButtonComponent";
import EditButtonComponent from "../../../../components/EditButtonComponent";
import DeleteButtonComponent from "../../../../components/DeleteButtonComponent";

const DimensionsDetail: React.FC = () => {
    const { menuType } = useParams<{ menuType: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const [expandedDimensions, setExpandedDimensions] = useState<Set<string>>(new Set());
    const [menuTypeId, setMenuTypeId] = useState<string | null>(null);

    // Get restaurant ID from Redux store
    const restaurantId = useSelector(
        (state: any) => state.restaurant.selectedRestaurantDetails.id
    );

    // Get menu types to find the ID for the current menu type
    const [triggerMenuTypes, { data: menuTypes = [] }] = useLazyGetMenuTypesQuery();

    // Delete dimension mutation
    const [deleteDimensionMutation, { isLoading: isDeleting }] = useDeleteDimensionMutation();
    
    // Delete dimension value mutation
    const [deleteDimensionValueMutation, { isLoading: isDeletingValue }] = useDeleteDimensionValueMutation();

    // Get dimensions for the current menu type
    const { data: productDimensions = [], isLoading, isError, refetch } = useGetDimensionsQuery(
        {
            restaurantId: restaurantId,
            customMenuItemTypeId: menuTypeId || ""
        },
        {
            skip: !restaurantId || !menuTypeId
        }
    );

    useEffect(() => {
        if (restaurantId && menuType) {
            triggerMenuTypes({ restaurantId });
        }
    }, [restaurantId, menuType, triggerMenuTypes]);

    useEffect(() => {
        if (menuTypes && menuTypes.length > 0 && menuType) {
            const foundMenuType = menuTypes.find(mt => mt.name === menuType);
            if (foundMenuType) {
                setMenuTypeId(foundMenuType.id);
            } else {
                console.warn("Menu type not found:", menuType);
            }
        }
    }, [menuTypes, menuType]);

    // Effect to handle refetch when returning from create/edit operations
    useEffect(() => {
        const state = location.state as { shouldRefetch?: boolean } | null;
        if (state?.shouldRefetch && menuTypeId && restaurantId) {
            refetch();
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location.state, refetch, menuTypeId, restaurantId, navigate, location.pathname]);

    const getFilteredDimensions = () => {
        return productDimensions || [];
    };

    const toggleDimensionExpansion = (dimensionId: string) => {
        const newExpanded = new Set(expandedDimensions);
        if (newExpanded.has(dimensionId)) {
            newExpanded.delete(dimensionId);
        } else {
            newExpanded.add(dimensionId);
        }
        setExpandedDimensions(newExpanded);
    };

    const handleDeleteDimension = async (dimensionId: string, dimensionName: string) => {
        Swal.fire({
            title: "Are you sure?",
            text: `This will permanently delete the dimension "${dimensionName}" and all its values. This action cannot be undone!`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, delete it!",
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    if (!restaurantId) {
                        toast.error("Restaurant not selected. Please refresh and try again.");
                        return;
                    }

                    await deleteDimensionMutation({
                        restaurantId: restaurantId,
                        dimensionId: dimensionId
                    }).unwrap();

                    toast.success(`Dimension "${dimensionName}" deleted successfully!`);

                    // Refresh the dimensions list
                    refetch();

                } catch (error: any) {
                    console.error("Error deleting dimension:", error);
                    const errorMessage = error?.data || "Failed to delete dimension. Please try again.";
                    toast.error(errorMessage);
                }
            }
        });
    };

    const handleDeleteDimensionValue = async (dimensionValueId: string, dimensionValueName: string, dimensionName: string) => {
        Swal.fire({
            title: "Are you sure?",
            text: `This will permanently delete the dimension value "${dimensionValueName}" from "${dimensionName}". This action cannot be undone!`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, delete it!",
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    if (!restaurantId) {
                        toast.error("Restaurant not selected. Please refresh and try again.");
                        return;
                    }

                    await deleteDimensionValueMutation({
                        restaurantId: restaurantId,
                        dimensionValueId: dimensionValueId
                    }).unwrap();

                    toast.success(`Dimension value "${dimensionValueName}" deleted successfully!`);

                    // Refresh the dimensions list to update the UI
                    refetch();

                } catch (error: any) {
                    console.error("Error deleting dimension value:", error);
                    const errorMessage = error?.data || "Failed to delete dimension value. Please try again.";
                    toast.error(errorMessage);
                }
            }
        });
    };

    const filteredDimensions = getFilteredDimensions();

    // Show loading if query is loading OR if required data (menuTypeId, restaurantId) hasn't been determined yet
    if (isLoading || !menuTypeId || !restaurantId) {
        return (
            <>
                <BreadCrumbs hasSubMenus={true} />
                <Card className="mt-3">
                    <CardBody>
                        <div className="text-center py-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="mt-2 text-muted">Loading dimensions...</p>
                        </div>
                    </CardBody>
                </Card>
            </>
        );
    }

    if (isError) {
        return (
            <>
                <BreadCrumbs hasSubMenus={true} />
                <Card className="mt-3">
                    <CardBody>
                        <div className="text-center py-5">
                            <p className="text-danger">Error loading dimensions. Please try again.</p>
                            <Button variant="primary" onClick={() => refetch()}>
                                Retry
                            </Button>
                        </div>
                    </CardBody>
                </Card>
            </>
        );
    }

    return (
        <>
            <BreadCrumbs hasSubMenus={true} />

            <Card className="mt-3">
                <CardHeader className="header-bg-color d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center">
                        <div>
                            <h5 className="mb-0">Product Dimensions for {menuType}</h5>
                            <small className="text-light opacity-75">
                                {filteredDimensions.length} dimension{filteredDimensions.length !== 1 ? 's' : ''} |
                                {filteredDimensions.reduce((total, dim) => total + (dim.dimensionValue?.length || 0), 0)} total values
                            </small>
                        </div>
                    </div>
                    <div className="d-flex gap-2">
                        <ButtonComponent
                            onClick={() => navigate(`/productCatalogue/product-groups/${menuType}/${menuTypeId}/add-dimension`)}
                            titel="Add Dimension"
                            icon={<FontAwesomeIcon icon={faPlus} />}
                        />
                    </div>
                </CardHeader>

                <CardBody>
                    {filteredDimensions.length === 0 ? (
                        <div className="text-center py-5">
                            <div className="mb-4">
                                <FontAwesomeIcon icon={faCog} size="3x" className="text-muted" />
                            </div>
                            <h5 className="text-muted mb-3">No dimensions configured for {menuType} yet.</h5>
                            <p className="text-muted mb-4">
                                Start by adding your first product dimension to enable customization options.
                            </p>
                            <Button
                                variant="primary"
                                onClick={() => navigate(`/productCatalogue/product-groups/${menuType}/${menuTypeId}/add-dimension`)}
                            >
                                <FontAwesomeIcon icon={faPlus} className="me-2" />
                                Add First Dimension
                            </Button>
                        </div>
                    ) : (
                        <div>
                            <div className="mb-4">
                                <p className="text-muted">
                                    Manage product dimensions and their values for <strong>{menuType}</strong> menu items.
                                    Dimensions define the customization options available to customers.
                                </p>
                            </div>

                            {[...filteredDimensions]
                                .sort((a, b) => a.selectionOrder - b.selectionOrder)
                                .map((dimension) => (
                                    <Card key={dimension.id} className="mb-4 border">
                                        <Card.Header
                                            className="d-flex justify-content-between align-items-center bg-light border-bottom"
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => toggleDimensionExpansion(dimension.id)}
                                        >
                                            <div className="d-flex align-items-center flex-grow-1">
                                                <FontAwesomeIcon
                                                    icon={expandedDimensions.has(dimension.id) ? faChevronDown : faChevronRight}
                                                    className="me-3 text-primary"
                                                />
                                                <div className="flex-grow-1">
                                                    <div className="d-flex align-items-center mb-1">
                                                        <h6 className="mb-0 text-dark me-3">{dimension.displayName}</h6>
                                                        <Badge bg={dimension.isRequired ? "danger" : "info"} className="me-2">
                                                            {dimension.isRequired ? "Required" : "Optional"}
                                                        </Badge>
                                                        <Badge bg="secondary">
                                                            Order: {dimension.selectionOrder}
                                                        </Badge>
                                                    </div>
                                                    <div className="d-flex flex-wrap gap-1 mt-2">
                                                        {dimension.dimensionValue && dimension.dimensionValue.length > 0 ? (
                                                            <>
                                                                {[...dimension.dimensionValue]
                                                                    .sort((a, b) => a.displayOrder - b.displayOrder)
                                                                    .slice(0, 3)
                                                                    .map((value) => (
                                                                        <span key={value.id} className="badge bg-light text-dark border me-1">
                                                                            {value.displayName}
                                                                        </span>
                                                                    ))}
                                                                {dimension.dimensionValue.length > 3 && (
                                                                    <span className="badge bg-light text-muted border">
                                                                        +{dimension.dimensionValue.length - 3} more
                                                                    </span>
                                                                )}
                                                            </>
                                                        ) : (
                                                            <span className="text-muted small">No values configured</span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="d-flex align-items-center gap-2" onClick={(e) => e.stopPropagation()}>
                                                <EditButtonComponent onClick={() => {
                                                    navigate(`/productCatalogue/product-groups/${menuType}/edit-dimension/${dimension.id}`);
                                                }} title="Edit Dimension" />
                                                <DeleteButtonComponent
                                                    onClick={() => {
                                                        handleDeleteDimension(dimension.id, dimension.displayName);
                                                    }}
                                                    title="Delete Dimension"
                                                    isDisabled={isDeleting}
                                                />
                                            </div>
                                        </Card.Header>

                                        {expandedDimensions.has(dimension.id) && (
                                            <Card.Body className="pt-4">
                                                <Row className="mb-4">
                                                    {/* <Col md={3}>
                                                        <strong>Dimension ID:</strong> {dimension.id.split('-').pop()}
                                                    </Col> */}
                                                    <Col md={4}>
                                                        <strong>Selection Order:</strong> {dimension.selectionOrder}
                                                    </Col>
                                                    <Col md={4}>
                                                        <strong>Required:</strong> {dimension.isRequired ? 'Yes' : 'No'}
                                                    </Col>
                                                    <Col md={4} className="d-flex justify-content-end">
                                                        <ButtonComponent
                                                            icon={<FontAwesomeIcon icon={faPlus} />}
                                                            titel="Add Value"
                                                            onClick={(e: any) => {
                                                                e.stopPropagation();
                                                                navigate(`/productCatalogue/product-groups/${menuType}/dimensions/${dimension.id}/add-value`);
                                                            }}
                                                        />
                                                    </Col>
                                                </Row>

                                                {dimension.dimensionValue && dimension.dimensionValue.length > 0 ? (
                                                    <>
                                                        <div className="table-responsive">
                                                            <table className="table table-hover mb-0">
                                                                <thead className="table-light">
                                                                    <tr>
                                                                        <th>Display Name</th>
                                                                        <th>Description</th>
                                                                        <th>Order</th>
                                                                        <th>Actions</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {[...dimension.dimensionValue]
                                                                        .sort((a, b) => a.displayOrder - b.displayOrder)
                                                                        .map((value) => (
                                                                            <tr key={value.id}>
                                                                                <td>
                                                                                    <strong className="text-primary">{value.displayName}</strong>
                                                                                </td>
                                                                                <td>
                                                                                    <span className="text-muted small">{value.description}</span>
                                                                                </td>
                                                                                <td>
                                                                                    <span className="badge bg-secondary">{value.displayOrder}</span>
                                                                                </td>
                                                                                <td>
                                                                                    <div className="d-flex gap-1" onClick={(e) => e.stopPropagation()}>
                                                                                        <EditButtonComponent
                                                                                            onClick={() => {
                                                                                                navigate(`/productCatalogue/product-groups/${menuType}/dimensions/${dimension.id}/edit-value/${value.id}`);
                                                                                            }}
                                                                                            title="Edit Value"
                                                                                        />
                                                                                        <DeleteButtonComponent
                                                                                            onClick={() => {
                                                                                                handleDeleteDimensionValue(value.id, value.displayName, dimension.displayName);
                                                                                            }}
                                                                                            title="Delete Value"
                                                                                            isDisabled={isDeletingValue}
                                                                                        />
                                                                                    </div>
                                                                                </td>
                                                                            </tr>
                                                                        ))}
                                                                </tbody>
                                                            </table>
                                                        </div>

                                                        {/* Quick Actions Row */}
                                                        <div className="mt-3 p-3 bg-light rounded">
                                                            <div className="d-flex justify-content-between align-items-center">
                                                                <small className="text-muted">
                                                                    <strong>Summary:</strong> {dimension.dimensionValue?.length || 0} value{(dimension.dimensionValue?.length || 0) !== 1 ? 's' : ''} |
                                                                    Created: {dimension.createdAt ? new Date(dimension.createdAt._seconds * 1000).toLocaleDateString() : 'N/A'}
                                                                </small>
                                                            </div>
                                                        </div>
                                                    </>
                                                ) : (
                                                    <div className="text-center py-4 bg-light rounded">
                                                        <p className="text-muted mb-2">No values configured for this dimension.</p>
                                                        <Button
                                                            variant="primary"
                                                            size="sm"
                                                            onClick={() => {
                                                                navigate(`/productCatalogue/product-groups/${menuType}/dimensions/${dimension.id}/add-value`);
                                                            }}
                                                        >
                                                            <FontAwesomeIcon icon={faPlus} className="me-1" />
                                                            Add First Value
                                                        </Button>
                                                    </div>
                                                )}
                                            </Card.Body>
                                        )}
                                    </Card>
                                ))}
                        </div>
                    )}
                </CardBody>
            </Card>
        </>
    );
};

export default DimensionsDetail;